<template>
  <div
    v-if="materials.length"
    class="flex flex-col gap-2.5"
    data-testid="training-materials"
  >
    <span
      :class="
        variant === 'modal'
          ? 'font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-black-60'
          : 'text-[13px] font-bold uppercase tracking-[0.08em] text-[#1F1F1F]/70 font-satoshi'
      "
    >
      {{ t('learn.materials.title') }}
    </span>
    <ul class="m-0 flex list-none flex-col gap-2 p-0">
      <li
        v-for="m in materials"
        :key="m.id"
        class="flex items-center gap-3 rounded-[12px] px-3.5 py-3"
        :class="variant === 'modal' ? 'border border-border-subtle bg-black-03' : 'bg-white/70'"
      >
        <span
          class="grid h-9 w-9 shrink-0 place-items-center rounded-[9px] bg-black-100 font-mono text-[10px] font-bold uppercase text-white"
          aria-hidden="true"
          >{{ extLabel(m.fileName) }}</span
        >
        <span class="flex min-w-0 flex-1 flex-col">
          <span class="truncate text-[14px] font-semibold leading-[18px] text-black-100 font-satoshi">{{
            m.title
          }}</span>
          <span class="truncate text-[12px] leading-[16px] text-black-60">{{ formatBytes(m.sizeBytes) }}</span>
        </span>
        <button
          type="button"
          class="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[10px] bg-gold-primary-100 px-3.5 text-[13px] font-bold text-black-100 transition-colors hover:bg-gold-primary-120 disabled:opacity-60"
          :disabled="downloadingId === m.id"
          :aria-label="t('learn.materials.downloadAria', { title: m.title })"
          :data-testid="`training-material-download-${m.id}`"
          @click="download(m)"
        >
          {{ hasAccess ? t('learn.materials.download') : t('learn.materials.unlock') }}
        </button>
      </li>
    </ul>
    <p
      v-if="error"
      class="text-[13px] text-red-600"
      role="alert"
    >
      {{ error }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { formatBytes } from '~/utils/training/fileSize';

type Material = { id: string; title: string; fileName: string; contentType: string; sizeBytes: number };

const props = withDefaults(defineProps<{ trainingId: string; variant?: 'page' | 'modal' }>(), { variant: 'page' });
const emit = defineEmits<{ upgrade: [] }>();

const { t } = useI18n();
const authStore = useAuthStore();

const materials = ref<Material[]>([]);
const hasAccess = ref(true);
const downloadingId = ref<string | null>(null);
const error = ref('');

const authHeaders = (): Record<string, string> =>
  authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {};

const extLabel = (fileName: string) => (/\.([a-z0-9]{1,4})$/i.exec(fileName)?.[1] ?? 'file').toUpperCase();

async function load(id: string) {
  materials.value = [];
  error.value = '';
  if (!id || !authStore.token) return;
  try {
    const res = await $fetch<{ data: { hasAccess: boolean; materials: Material[] } }>(
      `/api/trainings/${id}/materials`,
      { headers: authHeaders() },
    );
    if (id !== props.trainingId) return;
    materials.value = res.data.materials;
    hasAccess.value = res.data.hasAccess;
  } catch {
    materials.value = [];
  }
}

async function download(m: Material) {
  if (!hasAccess.value) {
    emit('upgrade');
    return;
  }
  downloadingId.value = m.id;
  error.value = '';
  try {
    const res = await $fetch<{ data: { url: string } }>(
      `/api/trainings/${props.trainingId}/materials/${m.id}/download`,
      { method: 'POST', headers: authHeaders() },
    );
    window.location.assign(res.data.url);
  } catch {
    error.value = t('learn.materials.error');
  } finally {
    downloadingId.value = null;
  }
}

watch(
  () => [props.trainingId, authStore.token] as const,
  ([id]) => load(id),
  { immediate: true },
);
</script>
